import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Box, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Alert,
  CircularProgress, MenuItem, Select, InputLabel, FormControl, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { formatDateOnly } from '../utils/dateTime';

const FRECUENCIA_LABELS = {
  semanal: 'Semanal',
  mensal: 'Mensal',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
};

const selectMenuProps = {
  slotProps: {
    paper: {
      sx: { maxWidth: 'none' },
    },
  },
};

function EquipamentoManager() {
  const [equipamentos, setEquipamentos] = useState([]);
  const [locais, setLocais] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [nome, setNome] = useState('');
  const [tipo_equipamento_id, setTipoEquipamentoId] = useState('');
  const [local_id, setLocalId] = useState('');
  const [data_aquisicao, setDataAquisicao] = useState('');
  const [frequencia_manutencao, setFrequencia] = useState('');

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get('/equipamentos?lista_leve=true'),
      api.get('/manutencao-locais'),
      api.get('/tipos-equipamento'),
    ])
      .then(([eqResponse, localResponse, tipoResponse]) => {
        setEquipamentos(eqResponse.data || []);
        setLocais(localResponse.data || []);
        setTipos(tipoResponse.data || []);
      })
      .catch((err) => {
        console.error('Erro ao carregar dados de equipamentos:', err);
        setError('Não foi possível carregar os dados.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setNome('');
    setTipoEquipamentoId('');
    setLocalId('');
    setDataAquisicao('');
    setFrequencia('');
    setDialogOpen(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');

    const payload = {
      nome: nome.trim(),
      tipo_equipamento_id,
      local_id,
      data_aquisicao: data_aquisicao || null,
      frequencia_manutencao: frequencia_manutencao || null,
    };

    const request = editingId
      ? api.put(`/equipamentos/${editingId}`, payload)
      : api.post('/equipamentos', payload);

    request
      .then(() => {
        resetForm();
        fetchAll();
      })
      .catch((err) => {
        console.error('Erro ao salvar equipamento:', err);
        setError(err.response?.data || 'Erro ao salvar equipamento.');
      })
      .finally(() => setSubmitting(false));
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setNome(item.nome);
    setTipoEquipamentoId(item.tipo_equipamento_id || '');
    setLocalId(item.local_id || '');
    setDataAquisicao(item.data_aquisicao || '');
    setFrequencia(item.frequencia_manutencao || '');
    setDialogOpen(true);
  };

  const handleNew = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este equipamento?')) return;
    api.delete(`/equipamentos/${id}`)
      .then(() => fetchAll())
      .catch((err) => {
        console.error('Erro ao deletar equipamento:', err);
        setError(err.response?.data || 'Erro ao deletar equipamento.');
      });
  };

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Novo equipamento
        </Button>
      </Box>

      <Dialog open={dialogOpen} onClose={resetForm} fullWidth maxWidth="md">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>{editingId ? 'Editar Equipamento' : 'Cadastro de Equipamento ou Recurso'}</DialogTitle>
          <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Equipamento (descrição)"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              fullWidth
              required
              placeholder="Ex.: Nobreak computador portaria"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={tipo_equipamento_id}
                onChange={(e) => setTipoEquipamentoId(e.target.value)}
                MenuProps={selectMenuProps}
              >
                {tipos.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.tipo}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>Local</InputLabel>
              <Select
                label="Local"
                value={local_id}
                onChange={(e) => setLocalId(e.target.value)}
                MenuProps={selectMenuProps}
              >
                {locais.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Data de Aquisição"
              type="date"
              value={data_aquisicao}
              onChange={(e) => setDataAquisicao(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Manutenção (opcional)</InputLabel>
              <Select
                label="Tipo de Manutenção (opcional)"
                value={frequencia_manutencao}
                onChange={(e) => setFrequencia(e.target.value)}
                MenuProps={selectMenuProps}
              >
                <MenuItem value="">Sem frequência definida</MenuItem>
                {Object.entries(FRECUENCIA_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetForm} disabled={submitting}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Equipamento</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Local</TableCell>
              <TableCell>Aquisição</TableCell>
              <TableCell>Frequência</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : equipamentos.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">Nenhum equipamento cadastrado.</TableCell></TableRow>
            ) : equipamentos.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.nome}</TableCell>
                <TableCell>{item.tipo_nome || '-'}</TableCell>
                <TableCell>{item.local_nome || '-'}</TableCell>
                <TableCell>{formatDateOnly(item.data_aquisicao)}</TableCell>
                <TableCell>{item.frequencia_manutencao ? (FRECUENCIA_LABELS[item.frequencia_manutencao] || item.frequencia_manutencao) : '-'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" aria-label="editar" onClick={() => handleEdit(item)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="deletar" onClick={() => handleDelete(item.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default EquipamentoManager;
